// frontend/src/context/AuthContext.jsx
// ============================================================
// 🔐 Contexte d'authentification centralisé (M26.21)
// Source unique de vérité pour user, rôle, et statut admin.
// ============================================================
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);        // { email, name, role }
  const [isAdmin, setIsAdmin] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);   // true pendant le check initial

  // Fetch unique du statut auth depuis le backend
  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/check");
      if (!res.ok) throw new Error("Auth check failed");
      const data = await res.json();

      if (data.authenticated && data.user) {
        setUser(data.user);
        setAuthenticated(true);
        setIsAdmin(data.user.role?.toUpperCase() === "ADMIN");
      } else {
        setUser(null);
        setAuthenticated(false);
        setIsAdmin(false);
      }
    } catch {
      setUser(null);
      setAuthenticated(false);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check au montage
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Appelé après un login Google réussi (depuis QuantumIdentityPage)
  const onLoginSuccess = useCallback(async () => {
    setLoading(true);
    await checkAuth();
  }, [checkAuth]);

  // Appelé après un logout
  const onLogout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } catch { /* ignore */ }
    setUser(null);
    setAuthenticated(false);
    setIsAdmin(false);
    // Nettoyer le legacy flag aussi
    localStorage.removeItem("admin_authenticated");
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isAdmin,
      authenticated,
      loading,
      onLoginSuccess,
      onLogout,
      refreshAuth: checkAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
